import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useAdminChallenges,
  type ChallengeListItem,
} from "../../hooks/useAdminChallenges";
import {
  SkillKeysByDiscipline,
  type DisciplineSlug,
  type ContentReviewStatus,
} from "@jumpbean/shared";
import { AIGenerationModal } from "../../components/admin/AIGenerationModal";

/**
 * ChallengesPage - Admin page to view and manage challenges with skill filtering.
 *
 * Features:
 * - Paginated challenge list with real data
 * - Filter by discipline, level, type, status, and skill tags
 * - Search by content
 * - Skill tags displayed as colored badges
 * - Quick actions per challenge
 * - "Generate with AI" button opens AI generation modal
 */
export function ChallengesPage() {
  const [searchInput, setSearchInput] = useState("");
  const {
    challenges,
    pagination,
    isLoading,
    error,
    filters,
    updateSearch,
    updateFilters,
    goToPage,
    deleteChallenge,
    refetch,
  } = useAdminChallenges();

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showAIModal, setShowAIModal] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    updateSearch(value);
  };

  const handleDisciplineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "" | DisciplineSlug;
    // Reset skill tags when discipline changes
    setSelectedSkills([]);
    updateFilters({
      discipline: value || undefined,
      skillTags: [],
    });
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    updateFilters({
      level: value ? parseInt(value, 10) : undefined,
    });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "" | ContentReviewStatus;
    updateFilters({ reviewStatus: value || undefined });
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    updateFilters({
      isActive: value === "" ? undefined : value === "true",
    });
  };

  const handleSkillTagToggle = (skillKey: string) => {
    const newSkills = selectedSkills.includes(skillKey)
      ? selectedSkills.filter((s) => s !== skillKey)
      : [...selectedSkills, skillKey];
    setSelectedSkills(newSkills);
    updateFilters({ skillTags: newSkills.length > 0 ? newSkills : undefined });
  };

  const handleDelete = async (challengeId: string) => {
    const result = await deleteChallenge(challengeId);
    if (result.success) {
      setDeleteConfirm(null);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSelectedSkills([]);
    updateFilters({
      discipline: undefined,
      level: undefined,
      type: undefined,
      reviewStatus: undefined,
      skillTags: undefined,
      isActive: undefined,
      search: undefined,
    });
  };

  // Get available skills based on selected discipline
  const availableSkills = filters.discipline
    ? SkillKeysByDiscipline[filters.discipline]
    : [];

  const hasActiveFilters =
    filters.discipline ||
    filters.level !== undefined ||
    filters.reviewStatus ||
    (filters.skillTags && filters.skillTags.length > 0) ||
    filters.isActive !== undefined ||
    filters.search;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Challenges</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage challenge content and skills
          </p>
        </div>
        <button
          className="
            inline-flex items-center gap-2 px-4 py-2
            bg-magic-600 text-white rounded-lg
            hover:bg-magic-700 transition-colors
            font-medium text-sm
          "
          onClick={() => setShowAIModal(true)}
        >
          <SparklesIcon className="w-5 h-5" />
          Generate with AI
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
        {/* Search and main filters row */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search challenge content..."
                className="
                  w-full pl-10 pr-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm placeholder-gray-400
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  focus:bg-white transition-colors
                "
              />
            </div>
          </div>

          <select
            value={filters.discipline || ""}
            onChange={handleDisciplineChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">All Disciplines</option>
            <option value="math">Math</option>
            <option value="spelling">Spelling</option>
            <option value="patterns">Patterns</option>
            <option value="typing">Typing</option>
            <option value="programming">Programming</option>
          </select>

          <select
            value={filters.level ?? ""}
            onChange={handleLevelChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">All Levels</option>
            {[1, 2, 3, 4, 5].map((level) => (
              <option key={level} value={level}>
                Level {level}
              </option>
            ))}
          </select>

          <select
            value={filters.reviewStatus || ""}
            onChange={handleStatusChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="needs_revision">Needs Revision</option>
          </select>

          <select
            value={filters.isActive === undefined ? "" : String(filters.isActive)}
            onChange={handleActiveChange}
            className="
              px-4 py-2
              bg-gray-50 border border-gray-200 rounded-lg
              text-sm text-gray-700
              focus:ring-2 focus:ring-magic-500 focus:border-transparent
            "
          >
            <option value="">Active & Inactive</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>

        {/* Skill tags filter (shown when discipline is selected) */}
        {filters.discipline && availableSkills.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Filter by Skills
            </p>
            <div className="flex flex-wrap gap-2">
              {availableSkills.map((skill) => (
                <button
                  key={skill}
                  onClick={() => handleSkillTagToggle(skill)}
                  className={`
                    px-3 py-1 rounded-full text-xs font-medium
                    transition-colors
                    ${
                      selectedSkills.includes(skill)
                        ? "bg-magic-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }
                  `}
                >
                  {formatSkillName(skill)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Clear filters */}
        {hasActiveFilters && (
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-magic-600 hover:text-magic-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Challenge table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Challenge
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Discipline / Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Skills
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-24" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-32" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-20" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        <div className="h-5 bg-gray-200 rounded-full w-16" />
                        <div className="h-5 bg-gray-200 rounded-full w-16" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded-full w-20" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-16 ml-auto" />
                    </td>
                  </tr>
                ))
              ) : challenges.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <EmptyIcon />
                      <p className="text-sm">
                        {hasActiveFilters
                          ? "No challenges match your filters"
                          : "No challenges found"}
                      </p>
                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="text-xs text-magic-600 hover:text-magic-700 font-medium"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                challenges.map((challenge) => (
                  <ChallengeRow
                    key={challenge.id}
                    challenge={challenge}
                    onDelete={() => setDeleteConfirm(challenge.id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-medium">
              {challenges.length > 0 ? pagination.offset + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {pagination.offset + challenges.length}
            </span>{" "}
            of <span className="font-medium">{pagination.total}</span> challenges
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page <= 1 || isLoading}
              className="
                px-3 py-1.5 text-sm font-medium
                text-gray-700 bg-white border border-gray-200 rounded-lg
                hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {getPageNumbers(pagination.page, pagination.totalPages).map(
                (pageNum, i) =>
                  pageNum === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum as number)}
                      disabled={isLoading}
                      className={`
                        px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                        ${
                          pageNum === pagination.page
                            ? "bg-magic-600 text-white"
                            : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                        }
                      `}
                    >
                      {pageNum}
                    </button>
                  )
              )}
            </div>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || isLoading}
              className="
                px-3 py-1.5 text-sm font-medium
                text-gray-700 bg-white border border-gray-200 rounded-lg
                hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* AI Generation modal */}
      <AIGenerationModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onSaveComplete={() => {
          // Refetch challenges to show newly created ones
          refetch();
        }}
      />
    </div>
  );
}

// ----- Helper Components -----

function ChallengeRow({
  challenge,
  onDelete,
}: {
  challenge: ChallengeListItem;
  onDelete: () => void;
}) {
  const disciplineColors: Record<string, string> = {
    math: "bg-magic-100 text-magic-700",
    spelling: "bg-pink-100 text-pink-700",
    patterns: "bg-purple-100 text-purple-700",
    typing: "bg-blue-100 text-blue-700",
    programming: "bg-green-100 text-green-700",
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">
              #{challenge.challengeNumber}
            </span>
            {challenge.aiMetadata && (
              <span className="inline-flex items-center gap-1 text-xs text-sunshine-600">
                <SparklesIcon className="w-3 h-3" />
                AI Generated
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          <span
            className={`
              inline-flex items-center px-2 py-0.5 rounded text-xs font-medium w-fit
              ${disciplineColors[challenge.disciplineSlug] || "bg-gray-100 text-gray-700"}
            `}
          >
            {challenge.disciplineName}
          </span>
          <span className="text-xs text-gray-500">
            Level {challenge.levelNumber}: {challenge.levelName}
          </span>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-900">{formatChallengeType(challenge.type)}</span>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1 max-w-xs">
          {challenge.skillTags.length === 0 ? (
            <span className="text-xs text-gray-400">No skills</span>
          ) : (
            challenge.skillTags.slice(0, 3).map((skill) => (
              <SkillBadge key={skill} skill={skill} discipline={challenge.disciplineSlug} />
            ))
          )}
          {challenge.skillTags.length > 3 && (
            <span className="text-xs text-gray-500 px-2 py-0.5">
              +{challenge.skillTags.length - 3} more
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={challenge.reviewStatus} isActive={challenge.isActive} />
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/admin/challenges/${challenge.id}`}
            className="text-sm font-medium text-magic-600 hover:text-magic-700"
          >
            Edit
          </Link>
          <button
            onClick={onDelete}
            className="text-sm font-medium text-red-600 hover:text-red-700"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function SkillBadge({
  skill,
  discipline,
}: {
  skill: string;
  discipline: string;
}) {
  const disciplineColors: Record<string, string> = {
    math: "bg-magic-50 text-magic-600 border-magic-200",
    spelling: "bg-pink-50 text-pink-600 border-pink-200",
    patterns: "bg-purple-50 text-purple-600 border-purple-200",
    typing: "bg-blue-50 text-blue-600 border-blue-200",
    programming: "bg-green-50 text-green-600 border-green-200",
  };

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
        border
        ${disciplineColors[discipline] || "bg-gray-50 text-gray-600 border-gray-200"}
      `}
    >
      {formatSkillName(skill)}
    </span>
  );
}

function StatusBadge({
  status,
  isActive,
}: {
  status: string;
  isActive: boolean;
}) {
  const statusStyles: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    needs_revision: "bg-orange-100 text-orange-700",
  };

  return (
    <div className="flex items-center gap-2">
      <span
        className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${statusStyles[status] || "bg-gray-100 text-gray-700"}
        `}
      >
        {formatStatusName(status)}
      </span>
      {!isActive && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          Inactive
        </span>
      )}
    </div>
  );
}

function DeleteConfirmModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Delete Challenge?
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          This will deactivate the challenge. It won't appear in the game but can be
          restored later.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- Helper Functions -----

function formatSkillName(skillKey: string): string {
  return skillKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatChallengeType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatStatusName(status: string): string {
  const statusNames: Record<string, string> = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    needs_revision: "Needs Revision",
  };
  return statusNames[status] || status;
}

function getPageNumbers(
  current: number,
  total: number
): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, "...", current - 1, current, current + 1, "...", total];
}

// ----- Icons -----

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

export default ChallengesPage;
